import type { UIViewInjectedProps } from '@uirouter/react';
import { notification, Form } from 'antd';
import { observer } from 'mobx-react-lite';
import moment from 'moment';

import { api } from '@/api';
import { DrawerForm } from '@/components/drawer-form';
import { getAllowedFormFields } from '@/components/generic-form/utils';
import { PERMISSION_OBJ_LOT } from '@/constants/permissions';
import { campaignStore, lotStore } from '@/stores';
import { ProductionStatus } from '@/types/enums';
import { getTranslatedString } from '@/utils';
import { withoutRepeatValue } from '@/utils/controller';
import { useMassUpdateCashedStores } from '@/utils/store';

import { getFormFields } from './setup';

export const LotMassCreateForm = observer(({ transition }: UIViewInjectedProps) => {
  const [form] = Form.useForm();

  const { loading } = useMassUpdateCashedStores([campaignStore]);

  const onClose = () => {
    transition.router.stateService.go('base-layout.lots');
  };

  const defaultCampaign =
    campaignStore.items.length > 0
      ? campaignStore.items.slice().sort((a, b) => Number(b.year) - Number(a.year))[0]
      : undefined;
  const defaultCampaignId = defaultCampaign?.id;

  const initValues = {
    count: 0,
    number: 1,
    productionDate: moment(new Date()),
    sacksQuantity: 385,
    weight: 25025,
    campaignId: defaultCampaignId,
    productionStatus: ProductionStatus.Produced,
  };
  const title = getTranslatedString('lots.mass-add-new');

  const onChangeLocation = (id: number) => {
    form.setFieldsValue({ locationId: id });
  };

  const formFields = getFormFields(onChangeLocation, true);
  let allowedFields = getAllowedFormFields(formFields, true, PERMISSION_OBJ_LOT, ['count']);

  if (defaultCampaign != null) {
    const campaignLabel = defaultCampaign.name?.trim() || defaultCampaign.year || String(defaultCampaign.id);
    allowedFields = allowedFields.map((field: any) => {
      if (field.name !== 'campaignId') return field;
      return {
        ...field,
        params: {
          ...field.params,
          initialSelectedOptions: [{ value: defaultCampaign.id, label: campaignLabel }],
        },
      };
    });
  }

  const create = async (values: any) => {
    const { number: startNumber, count, ...restOfValues } = values;

    const match = String(startNumber).match(/^(.*?)(\d+)$/);

    if (!match && count > 0) {
      const err = 'For mass creation, the lot number must end with digits.';
      notification.error({
        message: getTranslatedString('common.warning'),
        description: err,
      });
      return Promise.reject(new Error(err));
    }

    const prefix = match ? match[1] : startNumber;
    const numPart = match ? parseInt(match[2], 10) : null;
    const numLength = match ? match[2].length : 0;

    const params = [];
    for (let i = 0; i <= count; i++) {
      const currentLotNumber = numPart !== null ? `${prefix}${String(numPart + i).padStart(numLength, '0')}` : prefix;
      params.push({ ...restOfValues, number: currentLotNumber, isDraft: false });
    }

    try {
      const numbers: string[] = params.map(({ number }) => number);
      const {
        data: { invalid = [] },
      } = await api.lots.checkNumbers({ numbers }).source;

      if (invalid.length) {
        const errMsg: string = getTranslatedString('lots.mass-create-warn', { 0: invalid.join(', ') });

        form.setFields([
          {
            name: 'number',
            errors: [errMsg],
          },
        ]);

        form.scrollToField('number');
        return Promise.reject(new Error(getTranslatedString('lots.mass-create-warn2')));
      }

      const {
        data: { items = [] },
      } = await api.lots.createMany({ items: params }).source;
      return items.forEach((item: cyrian.api.lots.Lot) => {
        lotStore.addItem(item);
      });
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const resourceController = withoutRepeatValue(initValues, { create });

  return (
    <DrawerForm
      form={form}
      title={title}
      initValues={initValues}
      formFields={allowedFields}
      onClose={onClose}
      resourceController={resourceController}
      loaderCondition={loading}
    />
  );
});
